import {
  Button,
  ColorInput,
  Group,
  Image,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { useEffect, useRef, useState } from 'react';
import useFitText from 'use-fit-text';
import { v4 as uuid } from 'uuid';
import { exportAsBlob, exportAsImage } from '../../src/Utils';
import { TierPresets, TIER_COLOR_SWATCH } from '../GradientPicker/GradientPicker';
import { TierLabel } from './SetupTiersScreen';

export type TextToImageTypes = {
  value: string;
  bold: boolean;
  italics: boolean;
  underline: boolean;
  defaultColor: string;
};

export const TextToImageModal = ({
  onAddImage,
  onUpdateLocalImage,
  opts,
}: {
  onAddImage: (img: string, name: string, id: string) => void;
  onUpdateLocalImage?: (object: TextToImageTypes) => void;
  opts?: TextToImageTypes;
}) => {
  const [currentText, setCurrentText] = useState('');
  const [bold, setBold] = useState(false);
  const [underline, setUnderline] = useState(false);
  const [italics, setItalics] = useState(false);
  const fitText = useFitText({ maxFontSize: 1200, logLevel: 'debug', resolution: 10 });
  const theme = useMantineTheme();
  const [buttonColor, setButtonColor] = useState('violet');
  const [defaultColor, setDefaultColor] = useState(theme.colors.dark[6]);
  const [addError, setAddError] = useState(true);
  const selectedColor = theme.colors.violet[8];

  useEffect(() => {
    if (opts) {
      setBold(opts.bold);
      setUnderline(opts.underline);
      setItalics(opts.italics);
      setCurrentText(opts.value);
      setDefaultColor(opts.defaultColor);
    }
  }, []);
  const returnModal = async () => {
    if (!fitText || !fitText.ref.current) {
      return;
    }
    if (!defaultColor || !currentText) {
      setAddError(true);
      return;
    }

    const image = await exportAsImage(fitText.ref.current);
    const newId = uuid();
    setAddError(false);
    setButtonColor('green');
    setTimeout(() => {
      setButtonColor('violet');
    }, 1000);
    onAddImage(image, '', newId);
    if (onUpdateLocalImage) {
      onUpdateLocalImage({
        defaultColor,
        bold,
        italics,
        underline,
        value: currentText,
      });
      modals.closeAll();
    }
    setCurrentText('');
  };
  return (
    <Stack sx={{ gap: 3 }}>
      <Text>Preview:</Text>
      <TierLabel
        fitText={fitText}
        squareSize="100%"
        bold={bold}
        underline={underline}
        italics={italics}
        tier={{
          name: currentText,
          id: '0',
          textColor: 'white',
          backgroundColor: defaultColor,
          preset: TierPresets.DEFAULT,
        }}
      />
      <TextInput
        placeholder="Enter Text Here"
        value={currentText}
        data-autofocus
        size="lg"
        pt="sm"
        pb="xs"
        onChange={(event) => {
          setAddError(false);
          setCurrentText(event.currentTarget.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            returnModal();
          }
        }}
      />
      {(!defaultColor || !currentText) && addError && (
        <Text color="red">{`You don't have any text!`}</Text>
      )}
      <Group pr="lg" sx={{ justifyContent: 'space-between' }}>
        <Group
          w={120}
          h={25}
          sx={{
            gap: 0,
            display: 'flex',
            borderRadius: 10,
            border: '1px solid black',
            overflow: 'hidden',
            alignContent: 'center',
            justifyContent: 'space-between',
          }}
        >
          <UnstyledButton
            sx={{
              fontWeight: 'bolder',
              color: `${bold ? 'white' : 'black'}`,
              textAlign: 'center',
            }}
            p={0}
            w="32%"
            h="100%"
            onClick={() => setBold(!bold)}
            bg={bold ? selectedColor : 'white'}
          >
            B
          </UnstyledButton>
          <UnstyledButton
            sx={{
              fontWeight: 'bold',
              fontStyle: 'italic',
              color: `${italics ? 'white' : 'black'}`,
              textAlign: 'center',
            }}
            p={0}
            w="32%"
            h="100%"
            bg={italics ? selectedColor : 'white'}
            onClick={() => setItalics(!italics)}
          >
            i
          </UnstyledButton>
          <UnstyledButton
            sx={{
              fontWeight: 'bold',
              textDecorationLine: 'underline',
              textDecorationStyle: 'solid',
              background: 'white',
              textAlign: 'center',
              color: `${underline ? 'white' : 'black'}`,
            }}
            p={0}
            w="32%"
            h="100%"
            bg={underline ? selectedColor : 'white'}
            onClick={() => setUnderline(!underline)}
          >
            U
          </UnstyledButton>
        </Group>
        <ColorInput
          sx={{
            input: {
              color: theme.colors.dark[6],
              cursor: 'pointer',
              backgroundColor: 'rgba(0,0,0,0)',
              borderWidth: 0,
            },
          }}
          shadow="md"
          disallowInput
          withEyeDropper={false}
          withPicker={false}
          swatches={TIER_COLOR_SWATCH}
          w={10}
          value={defaultColor}
          swatchesPerRow={5}
          onChange={(color) => {
            setDefaultColor(color);
          }}
        />
      </Group>
      <Group sx={{ justifyContent: 'space-between' }}>
        <Button
          onClick={() => {
            modals.closeAll();
          }}
          variant="outline"
          mt="md"
        >
          Close
        </Button>
        <Button
          onClick={() => {
            returnModal();
          }}
          mt="md"
          color={buttonColor}
        >
          Add{buttonColor === 'green' ? 'ed' : ''}
        </Button>
      </Group>
    </Stack>
  );
};
